/**
 * OrderStepper — the Trioty 4-step order timeline (Order placed → Payment
 * confirmed → Shipped → Delivered), derived from the order stage with
 * done / active / upcoming states. Reused by the detail drawer (default) and
 * the hover tooltip (`compact`).
 */

import type { OrderStatus } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

type StepState = "done" | "active" | "upcoming";
type StepIconKind = "plus" | "card" | "truck" | "check";

/** Order stage → progress index (Trioty makeTimeline `prog`). */
function stageProg(status: OrderStatus): number {
  switch (status) {
    case "delivered":
      return 4;
    case "shipped":
      return 3;
    case "confirmed":
    case "preparing":
      return 2;
    case "pending_payment_verification":
    case "paid_less_than_required":
    case "pending_buyer_payment":
    case "pending_buyer_verification":
    case "pending_seller_confirmation":
      return 1;
    default:
      return 0; // draft, cancelled, declined
  }
}

const STEP_COLORS: Record<
  StepState,
  { dotBg: string; dotBorder: string; dotFg: string; line: string; title: string }
> = {
  done: {
    dotBg: "var(--accent-500)",
    dotBorder: "var(--accent-500)",
    dotFg: "var(--on-accent)",
    line: "var(--accent-500)",
    title: "var(--text-primary)",
  },
  active: {
    dotBg: "var(--card)",
    dotBorder: "var(--accent-500)",
    dotFg: "var(--text-primary)",
    line: "var(--line)",
    title: "var(--text-primary)",
  },
  upcoming: {
    dotBg: "var(--soft2)",
    dotBorder: "var(--line)",
    dotFg: "var(--text-disabled)",
    line: "var(--line)",
    title: "var(--text-muted)",
  },
};

function StepIcon({ kind, size }: { kind: StepIconKind; size: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (kind) {
    case "plus":
      return (
        <svg {...common}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "card":
      return (
        <svg {...common}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M2 10h20" />
        </svg>
      );
    case "truck":
      return (
        <svg {...common}>
          <path d="M10 17h4V5H2v12h3" />
          <path d="M14 8h4l3 3v6h-3" />
          <circle cx="7.5" cy="17.5" r="1.5" />
          <circle cx="17.5" cy="17.5" r="1.5" />
        </svg>
      );
    case "check":
    default:
      return (
        <svg {...common}>
          <path d="M20 6 9 17l-5-5" />
        </svg>
      );
  }
}

export function OrderStepper({
  status,
  placedSub,
  compact = false,
}: {
  status: OrderStatus;
  placedSub: string;
  compact?: boolean;
}) {
  const t = useTranslations();
  const prog = stageProg(status);
  const mk = (n: number): StepState => (prog < n ? "upcoming" : prog === n ? "active" : "done");
  const steps: { title: string; sub: string; state: StepState; icon: StepIconKind }[] = [
    { title: t(keys.orders.tlOrderPlaced), sub: placedSub, state: "done", icon: "plus" },
    {
      title: t(keys.orders.tlPaymentConfirmed),
      sub: prog >= 2 ? t(keys.orders.tlPaid) : t(keys.orders.moneyAwaiting),
      state: mk(1),
      icon: "card",
    },
    {
      title: t(keys.orderStatus.shipped),
      sub: prog >= 3 ? t(keys.orders.shipmentStatusInTransit) : t(keys.orders.tlPending),
      state: mk(2),
      icon: "truck",
    },
    {
      title: t(keys.orderStatus.delivered),
      sub: prog >= 4 ? t(keys.orders.tlCompleted) : t(keys.orders.tlPending),
      state: mk(3),
      icon: "check",
    },
  ];

  const dot = compact ? 22 : 26;
  const iconSize = compact ? 12 : 13;

  return (
    <div className="flex flex-col">
      {steps.map((step, i) => {
        const c = STEP_COLORS[step.state];
        return (
          <div key={step.title} className="flex" style={{ gap: compact ? 11 : 13 }}>
            <div className="flex shrink-0 flex-col items-center" style={{ width: dot }}>
              <span
                className="flex items-center justify-center rounded-full border-[1.5px]"
                style={{ width: dot, height: dot, background: c.dotBg, borderColor: c.dotBorder, color: c.dotFg }}
              >
                <StepIcon kind={step.icon} size={iconSize} />
              </span>
              {i < steps.length - 1 ? (
                <span
                  className="w-0.5 flex-1 rounded-full"
                  style={{ background: c.line, minHeight: compact ? 12 : 16, margin: `${compact ? 3 : 4}px 0` }}
                />
              ) : null}
            </div>
            <div className="min-w-0 flex-1" style={{ paddingBottom: compact ? 12 : 18 }}>
              <div
                className="font-semibold leading-[1.35]"
                style={{ color: c.title, fontSize: compact ? 12 : 13 }}
              >
                {step.title}
              </div>
              <div
                className="mt-0.5 leading-[1.4] text-[color:var(--text-muted)]"
                style={{ fontSize: compact ? 10.5 : 11.5 }}
              >
                {step.sub}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
